import NextLink from 'next/link';
import { type ComponentPropsWithRef, type JSX } from 'react';

import { type TitleCase } from '@craft/lib/formatters';

import { Button, type ButtonPartialProps, type ButtonProps } from './Button';
import { ButtonDiscreteSize, getLinkHref, type LinkComponent, type LinkHref } from './types';

export type LinkProps<
  E extends LinkComponent = typeof NextLink,
  O extends keyof ButtonPartialProps = never,
> = {
  /**
   * The internal path or external URL that the {@link Link} component should navigate to when
   * clicked, the form of which depends on the 'component' prop, {@link E}.
   *
   * @see {LinkHref}
   */
  readonly href: LinkHref<E>;
  readonly shouldOpenInNewTab?: boolean;
} & ButtonProps<E, Omit<ButtonPartialProps, O>, 'href'>;

type LinkComponentType = <E extends LinkComponent = typeof NextLink>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & LinkProps<E>,
) => JSX.Element;

type LinkPartialComponentType = <E extends LinkComponent = typeof NextLink>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & LinkProps<E, 'size'>,
) => JSX.Element;

const LocalLink: LinkComponentType &
  Partial<Record<TitleCase<ButtonDiscreteSize>, LinkPartialComponentType>> = <
  E extends LinkComponent = typeof NextLink,
>({
  component = NextLink as E,
  href,
  ref,
  shouldOpenInNewTab,
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & LinkProps<E>): JSX.Element => {
  const ps = { ...props, component, href: getLinkHref({ component, href }) } as ButtonProps<E>;
  return (
    <Button<E>
      {...ps}
      component={component}
      ref={ref}
      rel={ps.rel ?? (shouldOpenInNewTab ? 'noopener noreferrer' : undefined)}
      target={ps.target ?? (shouldOpenInNewTab ? '_blank' : undefined)}
    />
  );
};

const createIconLinkPartial = (size: ButtonDiscreteSize): LinkPartialComponentType => {
  const LinkPartial: LinkPartialComponentType = <E extends LinkComponent = typeof NextLink>({
    ref,
    ...props
  }: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & LinkProps<E, 'size'>) => (
    <LocalLink {...(props as LinkProps<E, 'size'>)} ref={ref} size={size} />
  );
  return LinkPartial;
};

LocalLink.Small = createIconLinkPartial(ButtonDiscreteSize.Small);
LocalLink.Medium = createIconLinkPartial(ButtonDiscreteSize.Medium);
LocalLink.Large = createIconLinkPartial(ButtonDiscreteSize.Large);

/**
 * A component that extends the internalized {@link Button} component specifically for cases where
 * the {@link Button} is being used to link to another page, either internally or externally.
 *
 * This component should **always** be used in the application when linking to another internal
 * or external page.
 *
 * If the component is being used to link to an internal page, the 'component' prop, {@link E},
 * should be set to {@link NextLink} (which it is by default).  If the component is being used to
 * link to an external page, the 'component' prop, {@link E}, should be set to 'a'.
 *
 * Depending on the value of the 'component' prop, {@link E}, the 'href' prop will be restricted,
 * such that it correctly points to an external or internal page, depending on the 'component' prop,
 * {@link E}.
 */
export const Link = LocalLink as LinkComponentType &
  Record<TitleCase<ButtonDiscreteSize>, LinkPartialComponentType>;
